import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';

import { Context } from '@/components/Store';
import { ActionTypes } from '@/components/Store.d';
import { config, errorMessages } from '@/lib/config';
import { useMessage } from '@/lib/hooks';
import { log } from '@/lib/log';
import { validateMessageOrigin } from '@/lib/util';

type MessageHandlerProps = {
  children?: React.ReactNode;
};

const MessageHandler = ({ children }: MessageHandlerProps) => {
  const navigate = useNavigate();
  const [, dispatch] = useContext(Context);

  /**
   * maps error message to error message key
   * @param {string} errorMessage
   * @returns {string | null}
   */
  function mapToErrorMessageKey(errorMessage: string): string | null {
    let msgKey = null;

    Object.entries(errorMessages).forEach(([key, value]): void => {
      if (
        'redirect' in value &&
        'response' in value &&
        (value.response === errorMessage || (typeof errorMessage === 'string' && errorMessage?.startsWith(value.response)))
      ) {
        msgKey = key;
      }
    });

    return msgKey;
  }

  useMessage((e: MessageEvent) => {
    log(`MessageHandler: Receiving message from ${e.origin}`, e.data);

    validateMessageOrigin(e.origin, true);

    if (!e.data?.error) return;

    const { error: errorMessage } = e.data;
    const redirectMessage = mapToErrorMessageKey(errorMessage);

    if (redirectMessage) {
      navigate({
        pathname: config.routes.error,
        search: `code=${redirectMessage}`
      });

      return;
    }

    const alertMessage = Object.values(errorMessages).find((value) => 'response' in value && value.response === errorMessage);

    if (alertMessage && 'message' in alertMessage && alertMessage.message) {
      dispatch({
        type: ActionTypes.showError,
        payload: alertMessage.message
      });

      return;
    }

    navigate(config.routes.error);
  });

  return children || null;
};

export { MessageHandler };
